<template>
  <a-modal
    v-model:visible="props.visible"
    :footer="false"
    centered
    @cancel="emit('update:visible', false)"
    :mask-closable="false"
  >
    <template #title>
      <div class="text-center text-blue">
        <h2 class="text-lg font-semibold flex items-center justify-center">
          <img src="@/assets/images/msg.png" alt="message" class="w-8 mr-2" />
          {{ $t('inquiry_details') }}
        </h2>
      </div>
    </template>
    <a-divider />

    <div class="space-y-4">
      <a-card size="small" v-if="isAdminInquiry">
        <a-descriptions class="no-bottom-padding-descriptions" :column="2" :colon="false">
          <a-descriptions-item :label="$t('company_name')">{{
            inquiry.company?.name
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('account_name')">{{
            inquiry.username
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('telephone')">{{
            inquiry.company?.tel
          }}</a-descriptions-item>
          <a-descriptions-item :label="$t('email')">{{
            inquiry.company?.email
          }}</a-descriptions-item>
        </a-descriptions>
      </a-card>
      <div class="space-x-4 break-all">
        <span
          v-if="getImportantClass(inquiry.importance)"
          :class="getImportantClass(inquiry.importance) + ' important relative bottom-[2px]'"
        >
          <span>{{ $t('important_ja') }}</span>
        </span>
        <span class="">{{ String(inquiry.id).padStart(5, '0') }}</span>
        <span class="">{{ inquiry.subject }}</span>
      </div>

      <div
        class="bg-[#EFF5FB] p-4 rounded-lg space-y-4 !max-h-[calc(100vh-350px)] overflow-y-auto whitespace-pre-line"
      >
        <div
          v-for="(message, index) in chatMessages"
          :key="index"
          class="flex items-center space-x-2"
          :class="{ 'justify-end': message.userId === userId }"
        >
          <template v-if="message.userId !== userId">
            <img
              v-if="isSenderDischarger(message.userRoles)"
              src="@/assets/images/user-chat.png"
              alt="user"
              class="w-10"
            />
            <img v-else src="@/assets/images/cs.png" alt="user" class="w-10" />
            <span class="bg-white p-2 rounded-lg shadow max-w-xs">
              <p>{{ message.content }}</p>
              <small class="text-gray-500">{{
                dayjs(message.createdDate).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm:ss')
              }}</small>
            </span>
            <br />
            <a-popover v-if="message.files?.length > 1" placement="topLeft">
              <template #content>
                <div v-if="message.files && message.files.length">
                  <div v-for="file in message.files" :key="file" class="inline-block">
                    <img
                      src="@/assets/images/file.jpg"
                      alt="file"
                      class="w-10 cursor-pointer"
                      @click="viewUploadedFile(file)"
                    />
                  </div>
                </div>
              </template>
              <img src="@/assets/images/file.jpg" alt="file" class="w-10 cursor-pointer" />
            </a-popover>
            <img
              v-else-if="message.files?.length"
              src="@/assets/images/file.jpg"
              alt="file"
              class="w-10 cursor-pointer"
              @click="viewUploadedFile(message.files[0])"
            />
          </template>
          <template v-else>
            <a-popover v-if="message.files?.length > 1" placement="topLeft">
              <template #content>
                <div v-if="message.files && message.files.length">
                  <div v-for="file in message.files" :key="file" class="inline-block">
                    <img
                      src="@/assets/images/file.jpg"
                      alt="file"
                      class="w-10 cursor-pointer"
                      @click="viewUploadedFile(file)"
                    />
                  </div>
                </div>
              </template>
              <img src="@/assets/images/file.jpg" alt="file" class="w-10 cursor-pointer" />
            </a-popover>
            <img
              v-else-if="message.files?.length"
              src="@/assets/images/file.jpg"
              alt="file"
              class="w-10 cursor-pointer"
              @click="viewUploadedFile(message.files[0])"
            />
            <span class="bg-blue-100 p-2 rounded-lg shadow max-w-xs">
              <p>{{ message.content }}</p>
              <small class="text-gray-500">{{
                dayjs(message.createdDate).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm:ss')
              }}</small>
            </span>
            <img
              v-if="isSenderDischarger(message.userRoles)"
              src="@/assets/images/user-chat.png"
              style="transform: scaleX(-1)"
              alt="user"
              class="w-10"
            />
            <img v-else src="@/assets/images/cs.png" alt="user" class="w-10" />
          </template>
        </div>
      </div>

      <div v-if="inquiry.status !== STATUS.CLOSED">
        <div class="relative">
          <div class="relative z-10">
            <a-textarea
              v-model:value="newMessage"
              :placeholder="t('Please enter your message.')"
              :auto-size="{ minRows: 3, maxRows: 5 }"
              :maxLength="512"
              class="pr-20 !border-b-0 !rounded-b-none focus:!ring-0 hover:!border-gray-300 focus:!border-gray-300"
            />
            <a-button
              type="primary"
              class="!absolute bottom-2 right-2 !bg-[#4294FE] z-20"
              shape="round"
              size="small"
              @click="sendMessage"
            >
              {{ $t('send') }}
            </a-button>
          </div>
          <div class="relative z-0 border border-t-0 border-gray-300 rounded-b-lg">
            <div class="flex justify-between items-center text-blue p-2 gap-x-4">
              <a-space
                class="flex-shrink-0"
                :class="uploadedFiles.length > 0 ? 'cursor-not-allowed' : 'cursor-pointer'"
                @click="handleClickUploadFile"
              >
                <PlusCircleFilled class="text-lg mt-[2px]" />
                {{ t('File upload') }}
              </a-space>

              <div v-if="uploadedFiles.length > 0" class="flex-grow overflow-hidden">
                <div
                  v-for="(file, index) in uploadedFiles"
                  :key="index"
                  class="flex items-center justify-between"
                >
                  <span class="cursor-pointer truncate mr-2" @click="() => viewFile(file)">{{
                    file.name
                  }}</span>
                  <CloseCircleFilled
                    class="text-xl cursor-pointer text-red-500 flex-shrink-0"
                    @click="removeFile(index)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isDischarger && !isAdminInquiry" class="flex space-x-2 mt-2">
          <a-button block class="bg-gray !text-white" @click="handleCancel">{{
            $t('cancel_inquiry')
          }}</a-button>
          <a-button type="primary" class="bg-blue" block @click="handleSolve">{{
            $t('solution')
          }}</a-button>
        </div>

        <div v-else class="flex space-x-2 mt-2">
          <a-button block class="bg-gray !text-white" @click="handleClose">{{
            $t('close_inquiry')
          }}</a-button>
          <a-button type="primary" class="bg-red" block @click="handleDelete">{{
            $t('delete')
          }}</a-button>
        </div>
      </div>
    </div>
  </a-modal>

  <FileUploadModal
    v-if="isUploadModalVisible"
    v-model:visible="isUploadModalVisible"
    :allow-same-name="false"
    :current-files="uploadedFiles"
    :max-files="1"
    :acceptFileTypes="'.jpg,.png,.pdf,.xlsx,.doc,.mp4,.mov'"
    :drag-message="t('drop_a_file_to_upload')"
    @upload="handleFileUpload"
  />
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { PlusCircleFilled, CloseCircleFilled } from '@ant-design/icons-vue';
import { Modal, message } from 'ant-design-vue';
import FileUploadModal from './FileUploadModal.vue';
import { useFileOpener } from '@/utils';
const { openFileInNewTab } = useFileOpener();
import { STATUS } from '@/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import { useRoute } from 'vue-router';
const route = useRoute();

import {
  getInquiryDetails,
  replyInquiry,
  updateInquiryStatus,
  deleteInquiry
} from '@/services/modules/inquiry';
import { useAuthStore } from '@/stores';
import { ROLE } from '@/constants';
import { formatModalTitle } from '@/utils/modules/message';
const authStore = useAuthStore();
const isAdmin = authStore.isAdmin;
const isDischarger = authStore.isDischarger;
const isTransporter = authStore.isTransporter;
const isCustomerSupport = computed(() => isAdmin || isTransporter);
const isAdminInquiry = (route.meta.isAdminInquiry ?? false) as boolean;
const userId = isAdminInquiry ? authStore.adminId : authStore.userId;
const isSenderDischarger = (roles: any[]) => {
  return roles.some((role) => role.role === ROLE.WASTE);
};
const { t } = useI18n();

const props = defineProps<{
  visible: boolean;
  inquiryId: number;
}>();

const emit = defineEmits(['update:visible', 'update']);

const inquiry = ref<any>({});
const chatMessages = ref<any[]>([]);

const fetchInquiryDetails = async () => {
  try {
    const response: any = await getInquiryDetails(props.inquiryId);
    inquiry.value = response;
    chatMessages.value = response.inquiryHistoryDtos;
  } catch (error) {
    console.error('Error fetching inquiry details:', error);
  }
};

if (props.inquiryId) fetchInquiryDetails();

let isUploadModalVisible = ref(false);
const showUploadModal = () => {
  isUploadModalVisible.value = true;
};

let uploadedFiles: any = ref([]);
const handleFileUpload = (files: any[]) => {
  uploadedFiles.value.push(...files);
};

const newMessage = ref('');

const sendMessage = async () => {
  if (newMessage.value.trim()) {
    const formData = new FormData();
    formData.append('content', newMessage.value);
    uploadedFiles.value.forEach((file: any) => {
      formData.append('files', file.originFileObj);
    });

    const totalSize = uploadedFiles.value.reduce((acc: number, file: any) => {
      return acc + file.size;
    }, 0);
    if (totalSize >= 100 * 1024 * 1024) {
      message.error(t('max_100MB_upload'));
      return;
    }

    formData.append('isAdminInquiry', isAdminInquiry.toString());

    try {
      await replyInquiry(props.inquiryId, formData);
      await fetchInquiryDetails();
      // scroll to bottom
      const chatContainer = document.querySelector('.bg-gray-100');
      chatContainer?.scrollTo(0, chatContainer.scrollHeight);
      newMessage.value = '';
      uploadedFiles.value = [];
      emit('update');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }
};

const getImportantClass: Function = inject('getImportantClass', () => {});

const viewFile = (file: any) => {
  let originFileObj = file?.originFileObj;
  openFileInNewTab(originFileObj);
};

const removeFile = (index: number) => {
  uploadedFiles.value.splice(index, 1);
};

const handleClickUploadFile = (event: MouseEvent) => {
  if (uploadedFiles.value.length > 0) {
    event.stopPropagation();
    return;
  }
  showUploadModal();
};

const viewUploadedFile = (file: any) => {
  let baseURL = import.meta.env.VITE_FILE_URL;
  let fullURL = baseURL + file;
  window.open(fullURL, '_blank');
};

const handleCancel = () => {
  let modal = Modal.confirm({
    title: t('MSG_28_2'),
    okText: t('cancel_button'),
    okType: 'danger',
    cancelText: t('yes'),
    cancelButtonProps: {
      type: 'primary',
      onClick: async () => {
        try {
          await deleteInquiry(props.inquiryId);
          emit('update');
          emit('update:visible', false);
          modal.destroy();
          message.success(t('Your inquiry has been canceled.'));
        } catch (error) {
          console.log(error);
        }
      }
    },
    okButtonProps: { onClick: () => modal.destroy() },
    centered: true,
    maskClosable: true
  });
  const title = t('MSG_28_2');
  modal.update({
    title: formatModalTitle(title)
  });
};

const handleSolve = () => {
  let modal = Modal.confirm({
    title: t('MSG_28_3'),
    okText: t('cancel_button'),
    okType: 'danger',
    cancelText: t('yes'),
    cancelButtonProps: {
      type: 'primary',
      onClick: async () => {
        try {
          await updateInquiryStatus(props.inquiryId, STATUS.CLOSED);
          emit('update');
          emit('update:visible', false);
          modal.destroy();
          message.success(t('Your inquiry has been resolved.'));
        } catch (error) {
          console.log(error);
        }
      }
    },
    okButtonProps: { onClick: () => modal.destroy() },
    centered: true,
    maskClosable: true
  });
  const title = t('MSG_28_3');
  modal.update({
    title: formatModalTitle(title)
  });
};

const handleClose = () => {
  let modal = Modal.confirm({
    title: t('MSG_28_4'),
    okText: t('cancel_button'),
    okType: 'danger',
    cancelText: t('yes'),
    cancelButtonProps: {
      type: 'primary',
      onClick: async () => {
        try {
          await updateInquiryStatus(props.inquiryId, STATUS.CLOSED);
          emit('update');
          emit('update:visible', false);
          modal.destroy();
          message.success(t('We have crawled your inquiry.'));
        } catch (error) {
          console.log(error);
        }
      }
    },
    okButtonProps: { onClick: () => modal.destroy() },
    centered: true,
    maskClosable: true
  });
  const title = t('MSG_28_4');
  modal.update({
    title: formatModalTitle(title)
  });
};

const handleDelete = () => {
  let modal = Modal.confirm({
    title: t('MSG_28_2'),
    okText: t('cancel_button'),
    okType: 'danger',
    cancelText: t('yes'),
    cancelButtonProps: {
      type: 'primary',
      onClick: async () => {
        try {
          await deleteInquiry(props.inquiryId);
          emit('update');
          emit('update:visible', false);
          modal.destroy();
          message.success(t('Your inquiry has been deleted.'));
        } catch (error) {
          console.log(error);
        }
      }
    },
    okButtonProps: { onClick: () => modal.destroy() },
    centered: true,
    maskClosable: true
  });
  const title = t('MSG_28_2');
  modal.update({
    title: formatModalTitle(title)
  });
};

onMounted(async () => {
  await nextTick();
  setTimeout(async () => {
    await nextTick();
    const chatContainer = document.querySelector('.bg-gray-100');
    chatContainer?.scrollTo({
      top: chatContainer.scrollHeight,
      behavior: 'smooth'
    });
  }, 100);
});
</script>

<style scoped lang="scss">
.important {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: #ffffff;
  text-align: center;
  vertical-align: middle;
  padding-top: 4px;
  display: inline-block;
}

.yellow {
  background-color: #ffc000;
}

.red {
  background-color: #f46666;
}

.blue {
  background-color: #4294ff;
}
</style>

<style lang="scss">
.no-bottom-padding-descriptions .ant-descriptions-row:last-child > td {
  padding-bottom: 0 !important;
}
</style>
